import Axios from 'axios';
import { API_BASE_URL, apiClient, throwApiError } from './apiClient';

export const MAINTENANCE_PATH = '/maintenance';
export const MAINTENANCE_RETURN_KEY = 'maintenance:returnPath';
export const MAINTENANCE_NOTICE_DISMISSED_KEY = 'maintenanceNotice:dismissedId';

type MaintenanceResponse = {
  status?: number;
  data?: unknown;
};

export function isMaintenanceResponse(response?: MaintenanceResponse | null) {
  if (!response) return false;
  return response.status === 503;
}

export function redirectToMaintenance() {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === MAINTENANCE_PATH) return;
  const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  localStorage.setItem(MAINTENANCE_RETURN_KEY, returnPath);
  window.location.assign(MAINTENANCE_PATH);
}

let interceptorSetup = false;

export function setupMaintenanceInterceptor() {
  if (interceptorSetup) return;

  Axios.interceptors.response.use(
    (response) => {
      if (isMaintenanceResponse(response)) redirectToMaintenance();
      return response;
    },
    (error) => {
      console.log(error);
      if (isMaintenanceResponse(error?.response)) redirectToMaintenance();
      return Promise.reject(error);
    }
  );

  interceptorSetup = true;
}

export async function pingMaintenanceStatus(timeoutMs = 5000): Promise<MaintenanceResponse> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${API_BASE_URL}/api/auth`, {
      credentials: 'include',
      signal: controller.signal,
    });

    let data: unknown = null;

    try {
      data = await response.json();
    } catch {
      data = null;
    }

    return {
      status: response.status,
      data,
    };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export async function fetchNextMaintenanceNotice() {
  const response = await apiClient.api['maintenance-notices'].next.get();
  if (response.error) {
    throwApiError(response.error.value, response.error.status);
  }
  if (!response.data?.id) {
    return null;
  }
  return response.data;
}

export function getDismissedMaintenanceNoticeId() {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(MAINTENANCE_NOTICE_DISMISSED_KEY);
}

export function dismissMaintenanceNotice(id?: string | null) {
  if (typeof window === 'undefined' || !id) return;
  localStorage.setItem(MAINTENANCE_NOTICE_DISMISSED_KEY, id);
}
